import React from 'react';
import { Profile } from '../../types';
import './ProfileHeader.css';

interface Props {
    profile: Profile;
    onDownload: () => void;
    isGenerating: boolean;
}

const ProfileHeader: React.FC<Props> = ({ profile, onDownload, isGenerating }) => {
    return (
        <header className="profile-header">
            <div className="avatar-wrapper">
                <img src="/assets/img/profile_img.jpg" alt={profile.name} className="profile-pic" loading="lazy" width="96" height="96" />
                <span className="online-dot" title="Open to work — Remote Flutter & Full-Stack roles" />
            </div>

            <h1 className="header-name">{profile.name}</h1>

            {profile.roles && (
                <div className="header-roles">
                    {profile.roles.map((role, i) => (
                        <span key={i} className="role-pill">{role}</span>
                    ))}
                </div>
            )}

            {profile.tagline && (
                <p className="header-tagline">{profile.tagline}</p>
            )}

            <p className="header-location">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                </svg>
                {profile.location}
            </p>
            <span className="hiring-badge">Open to remote work</span>

            <div className="header-contacts">
                <a href={`mailto:${profile.contact.email}`} className="contact-link" title="Email">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    Email
                </a>
                <a href={`tel:${profile.contact.phone}`} className="contact-link" title="Phone">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .1h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14.92v2z"/>
                    </svg>
                    Phone
                </a>
                {profile.contact.github && (
                    <a href={profile.contact.github} target="_blank" rel="noopener noreferrer" className="contact-link">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                        </svg>
                        GitHub
                    </a>
                )}
                {profile.contact.linkedin && (
                    <a href={profile.contact.linkedin} target="_blank" rel="noopener noreferrer" className="contact-link">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                        LinkedIn
                    </a>
                )}
                {profile.contact.linktree && (
                    <a href={profile.contact.linktree} target="_blank" rel="noopener noreferrer" className="contact-link">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                        </svg>
                        Links
                    </a>
                )}
            </div>

            <div className="header-actions">
                <button className="btn-primary" onClick={onDownload} disabled={isGenerating}>
                    {isGenerating ? (
                        <>
                            <span className="spinner-sm" />
                            Generating…
                        </>
                    ) : (
                        <>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            Download CV
                        </>
                    )}
                </button>
                <a href={`mailto:${profile.contact.email}`} className="btn-secondary">
                    Hire Me
                </a>
            </div>

            {profile.stats && (
                <div className="header-stats">
                    {profile.stats.map((stat, i) => (
                        <div key={i} className="stat-item">
                            <span className="stat-value">{stat.value}</span>
                            <span className="stat-label">{stat.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </header>
    );
};

export default React.memo(ProfileHeader);
